import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container } from '@mui/material';

// Client logo information with image paths
const clients = [
  { name: 'BankUnited', logo: '/assets/bankunited.png' },
  { name: 'BCBS', logo: '/assets/bcbs.png' },
  { name: 'CareyIntl', logo: '/assets/careyintl.png' },
  { name: 'Carnival', logo: '/assets/carnival.png' },
  { name: 'CSX', logo: '/assets/csx.png' },
  { name: 'Farmers', logo: '/assets/farmers.png' },
  { name: 'Hoerbiger', logo: '/assets/hoerbiger.png' },
  { name: 'Instec', logo: '/assets/instec.png' },
  { name: 'MainStreet', logo: '/assets/mainstreet.jpeg' },
  { name: 'OfficeDepot', logo: '/assets/OfficeDepot.png' },
  { name: 'OneBlood', logo: '/assets/oneblood.png' },
  { name: 'OneRPM', logo: '/assets/onerpm.png' },
  { name: 'SpinLaunch', logo: '/assets/spinlaunch.png' },
  { name: 'SteelCase', logo: '/assets/steelcase.jpeg' },
  { name: 'TeleComp', logo: '/assets/telecomp.png' },
  { name: 'TransUnion', logo: '/assets/transunion.png' },
  { name: 'Travellers', logo: '/assets/Travellers.png' },
  { name: 'VyStar', logo: '/assets/VyStar.png' },
  { name: 'Watsco', logo: '/assets/watsco.png' },
  { name: 'XclusiveYacht', logo: '/assets/xclusiveyacht.png' }

];

const ClientsBar = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    
    // Clone the content for seamless scrolling
    const content = scrollContainer.querySelector('.clients-content');
    if (content) {
      scrollContainer.appendChild(content.cloneNode(true));
    }
    
    // Animation function
    let animationId: number;
    let scrollPos = 0;
    
    const scroll = () => {
      if (!scrollContainer) return;
      
      scrollPos += 1.5; // Adjust speed here
      if (scrollPos >= scrollContainer.scrollWidth / 2) {
        scrollPos = 0;
      }
      
      scrollContainer.scrollLeft = scrollPos;
      animationId = requestAnimationFrame(scroll);
    };
    
    // Start animation
    animationId = requestAnimationFrame(scroll);
    
    // Pause on hover
    const handleMouseEnter = () => {
      cancelAnimationFrame(animationId);
    };
    
    const handleMouseLeave = () => {
      animationId = requestAnimationFrame(scroll);
    };
    
    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      if (scrollContainer) {
        scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);
  
  return (
    <Box 
      sx={{ 
        py: 5, 
        backgroundColor: 'white',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      }}
    >
      <Container maxWidth="lg" sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          component="h3" 
          align="center"
          sx={{ 
            fontWeight: 600,
            color: 'text.secondary',
            mb: 3
          }}
        >
          Trusted by Industry Leaders
        </Typography>
      </Container>
      
      <Box 
        ref={scrollRef}
        sx={{ 
          display: 'flex',
          overflow: 'hidden',
          width: '100%',
          position: 'relative',
          '&:before, &:after': {
            content: '""',
            position: 'absolute',
            top: 0,
            width: '100px',
            height: '100%',
            zIndex: 2,
          },
          '&:before': {
            left: 0,
            background: 'linear-gradient(to right, white, transparent)',
          },
          '&:after': {
            right: 0,
            background: 'linear-gradient(to left, white, transparent)',
          }
        }}
      >
        <Box 
          className="clients-content"
          sx={{ 
            display: 'flex',
            flexShrink: 0,
            gap: 6,
            py: 2,
            px: 4,
          }}
        >
          {clients.map((client, index) => (
            <Box 
              key={index}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '120px',
                height: '40px',
                opacity: 0.9,
                transition: 'all 1.3s ease',
                // filter: 'grayscale(100%)',
                '&:hover': {
                  opacity: 1,
                  filter: 'grayscale(0%)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              {/* Fallback to text if image fails to load */}
              <Box
                component="img"
                src={client.logo}
                alt={client.name}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const target = e.target as HTMLImageElement;
                  // Replace with a div containing the text when image fails to load
                  const parent = target.parentNode;
                  if (parent) {
                    const textElement = document.createElement('div');
                    textElement.style.fontWeight = '700';
                    textElement.style.fontSize = '1.1rem';
                    textElement.textContent = client.name;
                    parent.replaceChild(textElement, target);
                  }
                }}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ClientsBar;
